import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getFeaturedDeals } from '../services/api';
import { currencySymbol } from '../utils/currency';
import './FeaturedDeals.css';

function fmt(n) {
  const v = Number(n);
  return isFinite(v) ? v.toFixed(2) : '—';
}
function timeAgoShort(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return 'just now';
  const hrs = Math.floor(ms / 3600000);
  if (hrs < 1) return 'just now';
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}
// Refresh is daily: prices resync after the ~02:00 UTC price sweep and one
// category is re-crawled at 03:00 UTC — count down to the next 03:00 UTC.
function msUntilNextRefresh() {
  const now = new Date();
  const next = new Date(now);
  next.setUTCHours(3, 0, 0, 0);
  if (next.getTime() <= now.getTime()) next.setUTCDate(next.getUTCDate() + 1);
  return Math.max(0, next.getTime() - now.getTime());
}
function fmtCountdown(ms) {
  if (ms <= 0) return 'imminent';
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (h >= 48) return `${Math.floor(h / 24)}d ${h % 24}h`;
  return `${h}h ${m}m`;
}
// The category the daily rotation crawled in the last 24h, or null. When
// EVERY row is new (post-bootstrap full refresh) there's no rotation signal.
function todayCategory(rows) {
  if (!rows.length || rows.every(d => d.is_new)) return null;
  return rows.find(d => d.is_new && d.source_category)?.source_category || null;
}

// Live featured-deals grid, shared by the landing page and the dashboard.
//   variant  'landing' (full header + category tabs + footer, 8 cards)
//            'dashboard' (compact head, no tabs, 4 cards)
//   src      click-log source label baked into /r links by the API
//   fallback rendered instead of null when the API errors or has no deals
export default function FeaturedDeals({ variant = 'landing', src = 'landing_deal', onTrackThis, limit, fallback = null }) {
  const [deals, setDeals]         = useState([]);
  const [fetchedAt, setFetched]   = useState(null);
  const [loading, setLoading]     = useState(true);
  const [errored, setErrored]     = useState(false);
  const [activeCat, setActiveCat] = useState('All');
  const [countdown, setCountdown] = useState(() => fmtCountdown(msUntilNextRefresh()));

  const compact = variant === 'dashboard';
  const max = limit ?? (compact ? 5 : 8);

  useEffect(() => {
    let cancelled = false;
    getFeaturedDeals(src)
      .then(res => {
        if (cancelled) return;
        const rows = res.data.data || [];
        setDeals(rows);
        setFetched(res.data.prices_checked_at || res.data.fetched_at || null);
        // First view lands on today's refreshed category (freshest deals,
        // NEW TODAY badges); 'All' stays one tab away. Falls back to 'All'
        // when there's no fresh rotation (e.g. crawl failed today).
        const fresh = todayCategory(rows);
        if (fresh) setActiveCat(fresh);
      })
      .catch(() => { if (!cancelled) setErrored(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [src]);

  useEffect(() => {
    const id = setInterval(() => setCountdown(fmtCountdown(msUntilNextRefresh())), 60000);
    return () => clearInterval(id);
  }, []);

  if (errored || (!loading && deals.length === 0)) return fallback;

  // After a full-table bootstrap refresh every row is "new", so the badge and
  // today's-category highlight carry no signal — suppress them.
  const allNew = deals.length > 0 && deals.every(d => d.is_new);
  const todayCat = todayCategory(deals);

  // Group tabs by our rotation label when present (clean, stable set of 7);
  // BrightData's reported category is the fallback for pre-rotation rows.
  // Today's rotated category leads (it's also the default view), then 'All'.
  const catOf = d => d.source_category || d.category;
  const catList = Array.from(new Set(deals.map(catOf).filter(Boolean)));
  const cats = todayCat
    ? [todayCat, 'All', ...catList.filter(c => c !== todayCat)]
    : ['All', ...catList];
  const visible = activeCat === 'All' ? deals : deals.filter(d => catOf(d) === activeCat);
  const catLabel = c => (c === todayCat ? `↻ ${c}` : c);

  return (
    <section className={`l-deals${compact ? ' l-deals--compact' : ''}`} id={compact ? undefined : 'live-deals'}>
      {compact ? (
        <>
          <div className="l-deals-compact-head">
            <div className="l-section-label">LIVE DEALS · {fetchedAt ? `UPDATED ${timeAgoShort(fetchedAt).toUpperCase()}` : 'AU'}</div>
            <span className="l-deals-footer-pill l-mono">● NEXT REFRESH IN {countdown}</span>
          </div>
          {cats.length > 1 && (
            <div className="l-deals-cats l-deals-cats--compact">
              {cats.map(c => (
                <button
                  key={c}
                  className={`l-deals-cat${activeCat === c ? ' l-deals-cat--on' : ''}`}
                  onClick={() => setActiveCat(c)}
                >{catLabel(c)}</button>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          <div className="l-deals-header">
            <div className="l-section-label">LIVE DEALS · {fetchedAt ? `UPDATED ${timeAgoShort(fetchedAt).toUpperCase()}` : 'AU'}</div>
            <h2 className="l-deals-h2">Drops happening <em>right now</em></h2>
            <p className="l-deals-sub">Auto-curated from Amazon AU best-sellers. Prices re-checked daily.</p>
          </div>

          {/* Category tabs + today's refresh + region */}
          <div className="l-deals-toolbar">
            <div className="l-deals-cats">
              {cats.map(c => (
                <button
                  key={c}
                  className={`l-deals-cat${activeCat === c ? ' l-deals-cat--on' : ''}`}
                  onClick={() => setActiveCat(c)}
                >{catLabel(c)}</button>
              ))}
            </div>
            <div className="l-deals-toolbar-right">
              {todayCat && (
                <span className="l-deals-today l-mono">↻ TODAY'S REFRESH: {todayCat.toUpperCase()}</span>
              )}
              <div className="l-deals-region">
                <span>REGION</span>
                <strong>🇦🇺 AU</strong>
              </div>
            </div>
          </div>
        </>
      )}

      {loading ? (
        <div className="l-deals-grid">
          {Array.from({ length: max }).map((_, i) => (
            <div key={i} className="l-deal-card l-deal-card--skeleton" />
          ))}
        </div>
      ) : (
        <div className="l-deals-grid">
          {visible.slice(0, max).map(d => (
            <div key={d.asin} className="l-deal-card">
              <Link
                to={`/product/${d.asin}`}
                className="l-deal-card-inner"
              >
                <div className="l-deal-image-wrap">
                  {d.image_url ? <img src={d.image_url} alt={d.title} loading="lazy" width="480" height="480" /> : <span className="l-deal-image-ph">&#x1F4E6;</span>}
                  <span className="l-deal-badge">&darr;&nbsp;{Math.round(Number(d.discount_pct))}%</span>
                  {d.is_new && !allNew && <span className="l-deal-new">NEW TODAY</span>}
                </div>
                <div className="l-deal-body">
                  <div className="l-deal-name" title={d.title}>{d.title}</div>
                  <div className="l-deal-price-row">
                    <span className="l-deal-price l-mono">{currencySymbol(d.currency)}{fmt(d.current_price)}</span>
                    <span className="l-deal-strike l-mono">{currencySymbol(d.currency)}{fmt(d.original_price)}</span>
                  </div>
                  <div className="l-deal-save l-mono">
                    Save {currencySymbol(d.currency)}{fmt(d.original_price - d.current_price)}
                  </div>
                  {/* Evidence-gated server-side (migration 045): shown only
                      when this ASIN has enough tracked history to support the
                      claim. Most cards will not carry it until history accrues,
                      which is deliberate — a badge nobody can trust is worth
                      less than no badge. */}
                  {d.at_30d_low && (
                    <div className="l-deal-low l-mono" title={`We have tracked this price for ${Math.round(Number(d.history_span_days))} days and have not seen it lower.`}>
                      ◆ LOWEST IN 30D
                    </div>
                  )}
                  <div className="l-deal-checked l-mono">
                    ✓ PRICE CHECKED {timeAgoShort(d.checked_at || d.fetched_at).toUpperCase() || 'RECENTLY'}
                  </div>
                </div>
              </Link>
              <div className="l-deal-actions">
                {/* Track needs a canonical Amazon URL the preview scraper can parse.
                    d.amazon_url is the /r?u=…&src=… redirect (good for the
                    outbound links below, but extractASIN can't read the encoded ASIN
                    out of it). Build from d.asin — featured deals are AU-only. */}
                <button
                  className="l-deal-track-btn"
                  onClick={() => onTrackThis(`https://www.amazon.com.au/dp/${d.asin}?th=1`)}
                >＋ Track this</button>
                <a
                  href={d.amazon_url}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="l-deal-view-btn"
                >View at Amazon →</a>
              </div>
            </div>
          ))}
        </div>
      )}

      {!compact && (
        <>
          <div className="l-deals-footer">
            <span className="l-deals-footer-pill">● NEXT REFRESH IN {countdown}</span>
            <span className="l-deals-footer-note">Prices change constantly &middot; verify on Amazon before buying</span>
          </div>
          <div className="l-deals-price-disclaimer">
            <p>
              Product prices and availability are accurate as of the date/time indicated and are subject to change.
              Any price and availability information displayed on Amazon Australia at the time of purchase will apply
              to the purchase of this product.
            </p>
            <p>
              CERTAIN CONTENT THAT APPEARS ON THIS SITE COMES FROM AMAZON. THIS CONTENT IS PROVIDED &quot;AS IS&quot;
              AND IS SUBJECT TO CHANGE OR REMOVAL AT ANY TIME.
            </p>
          </div>
        </>
      )}
    </section>
  );
}
